import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  useTable,
  useSortBy,
  useGlobalFilter,
  usePagination,
  useFilters,
} from "react-table";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import { addIranSansFont } from "../../../../../assets/fonts/IRANSans-normal";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "jalali-moment";
import { Menu } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/20/solid";
import DateRangeFilterBox from "./DateRangeFilterBox";
import "../../../../../assets/fonts/Vazir-normal";
import {
  Search, X, FileSpreadsheet, FileText, Filter, CheckCircle2,
  XCircle, Layers, Sparkles, ChevronRight, ChevronLeft, Eye,
  Package, Clock,
} from "lucide-react";
import { useTranslation } from "react-i18next";

/* ================================================================
 *  Main
 * ================================================================ */
const PlansListTable = ({ userPlanList: data, isEnglish: isEnglishProp }) => {
  const { t, i18n } = useTranslation(["user"]);
  const isEnglish = isEnglishProp ?? i18n.language === "en";

  /* ---------- locale helper ---------- */
  const formatNumber = useCallback(
    (num) => {
      const n = Number(num || 0);
      return isEnglish
        ? n.toLocaleString("en-US")
        : n.toLocaleString("fa-IR");
    },
    [isEnglish]
  );

  const [selectedMinDate, setSelectedMinDate] = useState(null);
  const [selectedMaxDate, setSelectedMaxDate] = useState(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [minDate, setMinDate] = useState("");
  const [maxDate, setMaxDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(t("plansListTable.filterByStatus"));
  const [selectedDateField, setSelectedDateField] = useState("createdAt");

  const handleClearDateFilters = () => {
    setSelectedMinDate(null);
    setSelectedMaxDate(null);
    setSelectedDateField("createdAt");
  };

  /* ---------- Sort ---------- */
  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [data]);

  /* ---------- Columns ---------- */
  const columns = useMemo(
    () => [
      {
        Header: t("plansListTable.columns.row"),
        Cell: ({ row }) => (
          <span className={`text-[11px] tabular-nums text-slate-500 dark:text-slate-400 ${isEnglish && 'font-sans'}`}>
            {formatNumber(row.index + 1).toLocaleString('en-US')}
          </span>
        ),
        disableSortBy: true,
      },
      {
        Header: t("plansListTable.columns.planName"),
        accessor: (row) => row.plan?.name,
        Cell: ({ value }) => (
          <div className="flex items-center gap-2 justify-center">
            <span className="text-xs font-bold text-slate-800 dark:text-amber-100 truncate max-w-[160px]">
              {value || "—"}
            </span>
          </div>
        ),
      },
      {
        Header: t("plansListTable.columns.remainingTokens"),
        accessor: (row) => row?.remainingTokens,
        Cell: ({ value }) => (
          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-extrabold tabular-nums bg-teal-500/15 text-teal-700 dark:text-teal-300 border border-teal-500/30 ${isEnglish && 'font-sans'}`}>
            {formatNumber(value).toLocaleString('en-US')}
          </span>
        ),
      },
      {
        Header: t("plansListTable.columns.createdAt"),
        accessor: "createdAt",
        Cell: ({ value }) => (
          <span className={`inline-flex items-center gap-1 text-[11px] tabular-nums text-slate-600 dark:!text-slate-300 whitespace-nowrap ${isEnglish && 'font-sans'}`}>
            <Clock size={10} className="text-teal-600/70 dark:text-teal-300/70" />
            {moment.utc(value).local().locale("fa").format("jYYYY/jMM/jDD HH:mm").toLocaleString('en-US')}
          </span>
        ),
      },
      {
        Header: t("plansListTable.columns.endDate"),
        accessor: "endDate",
        Cell: ({ value }) =>
          value == null ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-500/15 text-emerald-700 dark:!text-emerald-300 border border-emerald-500/30">
              <Sparkles size={9} />
              {t("plansListTable.unlimited")}
            </span>
          ) : (
            <span className={`inline-flex items-center gap-1 text-[11px] tabular-nums text-slate-600 dark:!text-slate-300 whitespace-nowrap ${isEnglish && 'font-sans'}`}>
              <Clock size={10} className="text-rose-500/70" />
              {moment.utc(value).local().locale("fa").format("jYYYY/jMM/jDD HH:mm").toLocaleString('en-US')}
            </span>
          ),
      },
      {
        Header: t("plansListTable.columns.transactionId"),
        accessor: "transactionId",
        Cell: ({ value }) => (
          <span className={`text-[11px] font-mono tabular-nums text-slate-600 dark:!text-slate-300 truncate max-w-[120px] inline-block ${isEnglish && 'font-sans'}`} title={value}>
            {value.toLocaleString('en-US') || "—"}
          </span>
        ),
      },
      {
        Header: t("plansListTable.columns.status"),
        accessor: "isActive",
        Cell: ({ value }) =>
          value ? (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_6px_rgba(16,185,129,0.9)]" />
              {t("plansListTable.active")}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-bold bg-rose-500/15 text-rose-700 dark:text-rose-300 border border-rose-500/30">
              <span className="h-1.5 w-1.5 rounded-full bg-rose-500 shadow-[0_0_6px_rgba(244,63,94,0.9)]" />
              {t("plansListTable.inactive")}
            </span>
          ),
      },
    ],
    [t, isEnglish, formatNumber]
  );

  /* ---------- Date sync ---------- */
  useEffect(() => {
    if (selectedMinDate) {
      const m = moment
        .from(`${selectedMinDate.year}/${selectedMinDate.month}/${selectedMinDate.day}`, "fa", "YYYY/MM/DD")
        .startOf("day");
      setMinDate(m.toISOString());
    } else {
      setMinDate("");
    }

    if (selectedMaxDate) {
      const m = moment
        .from(`${selectedMaxDate.year}/${selectedMaxDate.month}/${selectedMaxDate.day}`, "fa", "YYYY/MM/DD")
        .endOf("day");
      setMaxDate(m.toISOString());
    } else {
      setMaxDate("");
    }
  }, [selectedMinDate, selectedMaxDate]);

  /* ---------- Filter ---------- */
  const filteredData = useMemo(() => {
    if (!Array.isArray(sortedData)) return [];
    return sortedData.filter((item) => {
      const dateFieldValue = item[selectedDateField] ? moment(item[selectedDateField]) : null;

      const matchesDate =
        (!minDate || (dateFieldValue && dateFieldValue.isSameOrAfter(moment(minDate)))) &&
        (!maxDate || (dateFieldValue && dateFieldValue.isSameOrBefore(moment(maxDate))));

      const matchesName =
        item?.plan?.name?.toLowerCase()?.includes(globalFilter?.toLowerCase()) ||
        item?.businessId?.toLowerCase()?.includes(globalFilter?.toLowerCase()) ||
        item?.transactionId?.toLowerCase()?.includes(globalFilter?.toLowerCase());

      const matchesStatus =
        statusFilter === "" ||
        (statusFilter === "true" && item.isActive === true) ||
        (statusFilter === "false" && item.isActive === false);

      return matchesName && matchesDate && matchesStatus;
    });
  }, [sortedData, globalFilter, minDate, maxDate, statusFilter, selectedDateField]);

  /* ---------- Table ---------- */
  const tableInstance = useTable(
    {
      columns,
      data: filteredData,
      initialState: { pageSize: 5 },
    },
    useGlobalFilter,
    useFilters,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = tableInstance;

  /* ---------- Exports ---------- */
  const exportToExcel = () => {
    const mappedData = filteredData.map((item) => ({
      [t("plansListTable.excel.columns.planName")]: item.plan.name,
      [t("plansListTable.excel.columns.remainingTokens")]: Number(item.remainingTokens).toLocaleString("en-US"),
      [t("plansListTable.excel.columns.createdAt")]: moment.utc(item.createdAt).local().locale("fa").format("jYYYY/jMM/jDD HH:mm"),
      [t("plansListTable.excel.columns.endDate")]:
        item.endDate == null
          ? t("plansListTable.unlimited")
          : moment.utc(item.endDate).local().locale("fa").format("jYYYY/jMM/jDD HH:mm"),
      [t("plansListTable.excel.columns.transactionId")]: item.transactionId,
    }));

    const ws = XLSX.utils.json_to_sheet(mappedData, { origin: "A2" });
    XLSX.utils.sheet_add_aoa(ws, [[t("plansListTable.excel.title")]], { origin: "A1" });
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
    ws["!cols"] = [{ wch: 25 }, { wch: 20 }, { wch: 25 }, { wch: 25 }, { wch: 25 }];

    const range = XLSX.utils.decode_range(ws["!ref"]);
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cellRef = XLSX.utils.encode_cell({ c: C, r: R });
        if (!ws[cellRef]) continue;
        if (!ws[cellRef].s) ws[cellRef].s = {};
        ws[cellRef].s.alignment = { horizontal: "right" };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t("plansListTable.excel.sheetName"));
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array", cellStyles: true });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(dataBlob, t("plansListTable.excel.fileName"));
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.setFont("Vazir");
    const tableColumn = [
      t("plansListTable.pdf.columns.planName"),
      t("plansListTable.pdf.columns.remainingTokens"),
      t("plansListTable.pdf.columns.createdAt"),
      t("plansListTable.pdf.columns.endDate"),
      t("plansListTable.pdf.columns.transactionId"),
    ];
    const tableRows = filteredData.map((item) => [
      item.plan.name,
      item.remainingTokens,
      moment.utc(item.createdAt).local().locale("fa").format("jYYYY/jMM/jDD HH:mm"),
      item.endDate == null
        ? t("plansListTable.unlimited")
        : moment.utc(item.endDate).local().locale("fa").format("jYYYY/jMM/jDD HH:mm"),
      item.transactionId,
    ]);

    autoTable(doc, {
      head: [tableColumn],
      body: tableRows,
      styles: { font: "Vazir", fontStyle: "normal", halign: "center" },
      headStyles: { halign: "center" },
    });
    doc.save(t("plansListTable.pdf.fileName"));
  };

  /* ---------- Reset ---------- */
  const resetFilters = () => {
    setGlobalFilter("");
    setStatusFilter("");
    setSelectedStatus(t("plansListTable.filterByStatus"));
    setSelectedMinDate(null);
    setSelectedMaxDate(null);
  };

  const hasActiveFilters =
    globalFilter || statusFilter || selectedMinDate || selectedMaxDate;

  /* ================================================================
   *  RENDER
   * ================================================================ */
  return (
    <div>

      {/* ============================================================
       *  Filters Row
       * ============================================================ */}
      <div className="space-y-3 mb-4">

        {/* Row 1: Search + Reset */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1 min-w-0 group">
            <Search
              size={14}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none"
            />
            <input
              id="search"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              placeholder={t("plansListTable.searchPlaceholder")}
              className="
                w-full pr-9 pl-3 py-2.5 rounded-xl text-sm
                bg-white/60 dark:bg-slate-800/60
                text-slate-800 dark:text-slate-100
                border border-teal-500/20 dark:border-teal-400/20
                focus:outline-none focus:ring-2 focus:ring-amber-300/60 focus:border-amber-300/60
                transition-all duration-200
                placeholder:text-slate-400 dark:placeholder:text-slate-500
              "
            />
            {globalFilter && (
              <button
                type="button"
                onClick={() => setGlobalFilter("")}
                aria-label={t("plansListTable.clearSearch")}
                className="absolute left-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-md text-slate-400 hover:text-rose-500 hover:bg-rose-500/10 transition-colors"
              >
                <X size={12} />
              </button>
            )}
          </div>

          <button
            onClick={resetFilters}
            disabled={!hasActiveFilters}
            title={t("plansListTable.resetTitle")}
            className={`
              shrink-0 flex items-center justify-center gap-1.5
              h-10 px-3 sm:px-4 rounded-xl
              text-xs font-bold
              transition-all duration-300
              whitespace-nowrap
              ${hasActiveFilters
                ? "text-amber-900 dark:text-amber-100 bg-gradient-to-l from-amber-300/70 to-amber-400/40 border border-amber-300/50 hover:from-amber-300 hover:to-amber-400 hover:scale-[1.03] active:scale-95 shadow-[0_4px_16px_-6px_rgba(251,191,36,0.6)]"
                : "text-slate-400 dark:text-slate-600 bg-slate-100/50 dark:bg-slate-800/40 border border-slate-200/50 dark:border-slate-700/50 cursor-not-allowed"
              }
            `}
          >
            <X size={13} />
            <span className="hidden sm:inline">{t("plansListTable.reset")}</span>
          </button>
        </div>

        {/* Row 2: Status + Date filter + Exports */}
        <div className="flex flex-wrap items-center gap-2">

          {/* Status dropdown */}
          <Menu as="div" className="relative inline-block text-right">
            <Menu.Button
              className="
                inline-flex items-center gap-2
                rounded-xl px-3 py-2.5
                text-xs sm:text-sm font-semibold
                text-slate-700 dark:text-slate-200
                bg-white/60 dark:bg-slate-800/60
                border border-teal-500/20 dark:border-teal-400/20
                backdrop-blur-md
                transition-all duration-200
                hover:border-amber-300/60
                hover:shadow-[0_4px_16px_-6px_rgba(251,191,36,0.4)]
                whitespace-nowrap
              "
            >
              <Filter size={13} className="text-teal-600/70 dark:text-teal-300/70 shrink-0" />
              <span className="truncate max-w-[140px]">{selectedStatus}</span>
              <ChevronDownIcon className="-ml-1 size-4 text-slate-400 shrink-0" />
            </Menu.Button>

            <Menu.Items
              className="
                absolute right-0 z-30 mt-2 min-w-[180px]
                origin-top-right rounded-xl overflow-hidden
                bg-white/95 dark:bg-slate-900/95
                backdrop-blur-xl
                shadow-[0_20px_60px_-20px_rgba(15,118,110,0.5)]
                dark:shadow-[0_20px_60px_-20px_rgba(0,0,0,0.9)]
                border border-teal-500/20 dark:border-teal-400/20
                focus:outline-none
              "
            >
              <div className="h-[2px] bg-gradient-to-l from-transparent via-amber-400/70 to-transparent" />
              <div className="py-1">
                {[
                  { key: "all", value: "", icon: Layers, tone: "text-slate-500" },
                  { key: "active", value: "true", icon: CheckCircle2, tone: "text-emerald-500" },
                  { key: "inactive", value: "false", icon: XCircle, tone: "text-rose-500" },
                ].map(({ key, value, icon: Icon, tone }) => (
                  <Menu.Item key={key}>
                    {({ active }) => (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedStatus(t(`plansListTable.statusOptions.${key}`));
                          setStatusFilter(value);
                        }}
                        className={`
                          flex w-full items-center justify-end gap-2
                          px-4 py-2.5 text-xs sm:text-sm font-medium
                          text-slate-700 dark:text-slate-200
                          transition-colors duration-200
                          ${active ? "bg-amber-300/20 dark:bg-amber-400/10" : ""}
                          ${statusFilter === value ? "bg-teal-500/10 dark:bg-teal-400/10" : ""}
                        `}
                      >
                        <span>{t(`plansListTable.statusOptions.${key}`)}</span>
                        <Icon size={14} className={tone} />
                      </button>
                    )}
                  </Menu.Item>
                ))}
              </div>
            </Menu.Items>
          </Menu>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Export buttons */}
          <button
            onClick={exportToExcel}
            title={t("plansListTable.exportExcel")}
            className="
              group shrink-0
              h-10 px-3 sm:px-4 rounded-xl
              flex items-center justify-center gap-1.5
              text-xs font-bold
              text-emerald-700 dark:text-emerald-300
              bg-emerald-500/10 hover:bg-emerald-500/20
              border border-emerald-500/30 hover:border-emerald-400/60
              backdrop-blur-md
              transition-all duration-200
              hover:scale-105 active:scale-95
            "
          >
            <FileSpreadsheet size={14} />
            <span className="hidden sm:inline">{t("plansListTable.exportExcelShort")}</span>
          </button>

          <button
            onClick={exportToPDF}
            title={t("plansListTable.exportPdf")}
            className="
              group shrink-0
              h-10 px-3 sm:px-4 rounded-xl
              flex items-center justify-center gap-1.5
              text-xs font-bold
              text-rose-700 dark:text-rose-300
              bg-rose-500/10 hover:bg-rose-500/20
              border border-rose-500/30 hover:border-rose-400/60
              backdrop-blur-md
              transition-all duration-200
              hover:scale-105 active:scale-95
            "
          >
            <FileText size={14} />
            <span className="hidden sm:inline">{t("plansListTable.exportPdfShort")}</span>
          </button>
        </div>
      </div>

      {/* ============================================================
       *  Table
       * ============================================================ */}
      <div className="overflow-x-auto rounded-xl border border-teal-500/15 dark:border-teal-400/15">
        <table
          {...getTableProps()}
          className="w-full min-w-[760px] text-sm"
        >
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr
                key={headerGroup.id}
                {...headerGroup.getHeaderGroupProps()}
                className="bg-gradient-to-l from-teal-500/5 via-transparent to-amber-300/5 border-b border-teal-500/15 dark:border-teal-400/15"
              >
                {headerGroup.headers.map((column) => (
                  <th
                    key={column.id}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className="
                      px-3 py-3 text-center text-[11px] font-bold
                      text-slate-500 dark:text-slate-400 tracking-wide
                      cursor-pointer whitespace-nowrap select-none
                      hover:text-amber-600 dark:hover:text-amber-300
                      transition-colors duration-200
                    "
                  >
                    <span className="inline-flex items-center gap-1">
                      <span className="h-1 w-1 rounded-full bg-amber-400" />
                      {column.render("Header")}
                      {column.isSorted && (
                        <span className="text-[9px] text-teal-500">
                          {column.isSortedDesc ? "▼" : "▲"}
                        </span>
                      )}
                    </span>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="py-14 text-center">
                  <div className="flex flex-col items-center gap-2 text-slate-400 dark:text-slate-500">
                    <div className="h-14 w-14 rounded-full border-2 border-dashed border-teal-400/40 flex items-center justify-center">
                      <Layers size={22} className="animate-pulse text-teal-400/70" />
                    </div>
                    <p className="text-sm font-bold text-slate-500 dark:text-slate-400">
                      {t("plansListTable.emptyState.title")}
                    </p>
                    <p className="text-[11px] text-slate-400 dark:text-slate-500">
                      {t("plansListTable.emptyState.hint")}
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              page.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    key={row.id || row.original.id}
                    {...row.getRowProps()}
                    className="border-b border-teal-500/8 dark:border-teal-400/8 last:border-0 hover:bg-teal-500/5 dark:hover:bg-teal-400/5 transition-colors duration-200"
                  >
                    {row.cells.map((cell) => (
                      <td
                        key={cell.column.id}
                        {...cell.getCellProps()}
                        className="px-3 py-3 text-center whitespace-nowrap"
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ============================================================
       *  Pagination
       * ============================================================ */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 pt-4 border-t border-teal-500/10 dark:border-teal-400/10">

        {/* Rows per page */}
        <div className="flex items-center gap-2 text-[11px] sm:text-xs">
          <span className="text-slate-500 dark:text-slate-400 whitespace-nowrap">
            {t("plansListTable.pagination.rowsPerPage")}
          </span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="
              appearance-none px-2.5 py-1.5 rounded-lg
              text-xs font-bold tabular-nums
              bg-white/60 dark:bg-slate-800/60
              text-slate-700 dark:text-slate-200
              border border-teal-500/20 dark:border-teal-400/20
              focus:outline-none focus:ring-2 focus:ring-amber-300/60
              cursor-pointer
            "
          >
            {[5, 10, 20].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        {/* Page controls */}
        <div className="flex items-center gap-1.5">
          <button
            onClick={previousPage}
            disabled={!canPreviousPage}
            className="
              h-9 px-3 rounded-lg
              flex items-center gap-1
              text-xs font-bold
              text-teal-700 dark:text-amber-200
              bg-white/60 dark:bg-white/5
              border border-teal-500/20 dark:border-teal-400/20
              hover:border-amber-300/60 hover:bg-amber-300/10
              transition-all duration-200
              hover:scale-105 active:scale-95
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
            "
          >
            <ChevronRight size={14} />
            <span className="hidden sm:inline">{t("plansListTable.pagination.prev")}</span>
          </button>

          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/60 dark:bg-white/5 border border-teal-500/20 dark:border-teal-400/20">
            <span className="text-[11px] text-slate-500 dark:text-slate-400">
              {t("plansListTable.pagination.page")}
            </span>
            <span className="text-xs font-extrabold text-amber-600 dark:text-amber-300 tabular-nums">
              {formatNumber(pageIndex + 1)}
            </span>
          </div>

          <button
            onClick={nextPage}
            disabled={!canNextPage}
            className="
              h-9 px-3 rounded-lg
              flex items-center gap-1
              text-xs font-bold
              text-teal-700 dark:text-amber-200
              bg-white/60 dark:bg-white/5
              border border-teal-500/20 dark:border-teal-400/20
              hover:border-amber-300/60 hover:bg-amber-300/10
              transition-all duration-200
              hover:scale-105 active:scale-95
              disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100
            "
          >
            <span className="hidden sm:inline">{t("plansListTable.pagination.next")}</span>
            <ChevronLeft size={14} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PlansListTable;